import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Download, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportViewer = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const element = reportRef.current;
      const opt = {
        margin: 0,
        filename: `AEL_Report_${reportData.month}_${reportData.year}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(element).save();
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Report not found</h2>
          <Button onClick={() => navigate('/')}>Go Back</Button>
        </div>
      </div>
    );
  }

  const data = reportData.extracted_data;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="sticky top-0 z-10 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-xl font-bold">Report Viewer</h1>
          {/* PDF download hidden — keep handleDownloadPDF for future use */}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div
          ref={reportRef}
          className="bg-white shadow-2xl mx-auto"
          style={{ width: '210mm', minHeight: '297mm' }}
          data-testid="report-content"
        >
          {/* Page 1 */}
          <div className="p-12" style={{ minHeight: '297mm', position: 'relative' }}>
            {/* Header */}
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_8cf0233c-34c4-4c9e-9fe0-7f998765066d/artifacts/key4qenq_Logo.png"
                alt="Ellerston Capital"
                className="h-12 mb-6"
              />
              <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Arial, sans-serif' }}>
                Ellerston Australian Emerging Leaders Fund - Class A Units
              </h1>
              <p className="text-lg text-slate-600 mb-1">
                Monthly Report as at 30 {reportData.month} {reportData.year}
              </p>
              <p className="text-sm text-slate-500">APIR Code: ECL8784U | ARSN 647 979 333</p>
            </div>

            {/* Intro Text with Icons */}
            <div className="mb-8 space-y-3">
              <div className="flex items-start gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_report-wizard-58/artifacts/pj964ivc_Portfolio%20Make-up%20Icon.png" 
                  alt="" 
                  className="w-6 h-6 mt-0.5 flex-shrink-0"
                />
                <p className="text-sm leading-relaxed">Portfolio of 30-60 smaller companies built through an active, research-driven investment approach.</p>
              </div>
              <div className="flex items-start gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_report-wizard-58/artifacts/uai63mb8_Checklist%20Icon.png" 
                  alt="" 
                  className="w-6 h-6 mt-0.5 flex-shrink-0"
                />
                <p className="text-sm leading-relaxed">Focus on companies which have a sound business franchise with an attractive earnings profile, which can deliver growth in industries and trade at a discount to valuation.</p>
              </div>
              <div className="flex items-start gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_report-wizard-58/artifacts/wsa8f9j9_Graph%20Icon.png" 
                  alt="" 
                  className="w-6 h-6 mt-0.5 flex-shrink-0"
                />
                <p className="text-sm leading-relaxed">Aims to outperform the Benchmark over a rolling three-year period.</p>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-3">Performance Summary</h2>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-slate-800">
                    <th className="text-left py-2 font-bold">Performance</th>
                    <th className="text-right py-2 font-bold">1 Month</th>
                    <th className="text-right py-2 font-bold">3 Months</th>
                    <th className="text-right py-2 font-bold">1 Year</th>
                    <th className="text-right py-2 font-bold">3 Years (p.a.)</th>
                    <th className="text-right py-2 font-bold">Since Inception (p.a.)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="py-2">Net^</td>
                    <td className="text-right">{data.performance?.net?.['1m'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.net?.['3m'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.net?.['1y'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.net?.['3y_pa'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.net?.['inception_pa'] || 'N/A'}</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-2">Benchmark^</td>
                    <td className="text-right">{data.performance?.benchmark?.['1m'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.benchmark?.['3m'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.benchmark?.['1y'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.benchmark?.['3y_pa'] || 'N/A'}</td>
                    <td className="text-right">{data.performance?.benchmark?.['inception_pa'] || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-bold">Alpha</td>
                    <td className="text-right font-bold">{data.performance?.alpha?.['1m'] || 'N/A'}</td>
                    <td className="text-right font-bold">{data.performance?.alpha?.['3m'] || 'N/A'}</td>
                    <td className="text-right font-bold">{data.performance?.alpha?.['1y'] || 'N/A'}</td>
                    <td className="text-right font-bold">{data.performance?.alpha?.['3y_pa'] || 'N/A'}</td>
                    <td className="text-right font-bold">{data.performance?.alpha?.['inception_pa'] || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-2">^S&P/ASX Small Ordinaries Accumulation Index. **Inception date is 16 August 2021.</p>
            </div>

            {/* Two Column Section */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Left Column */}
              <div>
                {/* Key Information */}
                <div className="mb-6 bg-slate-50 p-4 rounded">
                  <h3 className="text-base font-bold mb-3">Key Information</h3>
                  <div className="text-xs space-y-2">
                    <div><span className="font-semibold">Portfolio Manager(s):</span> David Keelan, Melanie Clarke, James Barker</div>
                    <div><span className="font-semibold">Investment Objective:</span> To outperform the Benchmark over a rolling three year period.</div>
                    <div><span className="font-semibold">Benchmark:</span> S&P/ASX Small Ordinaries Accumulation Index</div>
                    <div><span className="font-semibold">Liquidity:</span> Daily</div>
                    <div><span className="font-semibold">Target Number of Holdings:</span> 30-60</div>
                    <div><span className="font-semibold">Minimum Investment:</span> $10,000</div>
                    <div><span className="font-semibold">Distribution Frequency:</span> Half-yearly</div>
                    <div><span className="font-semibold">Management Fee:</span> 1.10% (p.a.)</div>
                    <div><span className="font-semibold">Performance Fee:</span> 20.00%</div>
                    <div><span className="font-semibold">Buy/Sell Spread:</span> 0.25% / 0.25%</div>
                  </div>
                </div>

                {/* Top Holdings */}
                <div className="mb-6">
                  <h3 className="text-base font-bold mb-3">Top Holdings*</h3>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-300">
                        <th className="text-left py-1 font-bold">Company</th>
                        <th className="text-left py-1 font-bold">Sector</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_holdings?.slice(0, 5).map((holding, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-1">{holding.company}</td>
                          <td className="py-1 text-slate-600">{holding.sector}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-400 mt-1">*In alphabetical order</p>
                </div>

                {/* Key Metrics */}
                <div>
                  <h3 className="text-base font-bold mb-3">Key Portfolio Metrics</h3>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-300">
                        <th className="text-left py-1 font-bold">FY26*</th>
                        <th className="text-right py-1 font-bold">Fund</th>
                        <th className="text-right py-1 font-bold">Benchmark</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-1">Price/Earnings</td>
                        <td className="text-right">{data.key_metrics?.fund?.pe || 'N/A'}</td>
                        <td className="text-right">{data.key_metrics?.benchmark?.pe || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-1">Dividend Yield</td>
                        <td className="text-right">{data.key_metrics?.fund?.dividend_yield || 'N/A'}</td>
                        <td className="text-right">{data.key_metrics?.benchmark?.dividend_yield || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="py-1">Net Debt/EBITDA</td>
                        <td className="text-right">{data.key_metrics?.fund?.net_debt_ebitda || 'N/A'}</td>
                        <td className="text-right">{data.key_metrics?.benchmark?.net_debt_ebitda || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Right Column */}
              <div>
                {/* Sector Allocation */}
                <div className="mb-6">
                  <h3 className="text-base font-bold mb-3">
                    Sector Allocation
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.sector_allocation} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="sector" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={80} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="weight" fill="#A8A67A" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Market Cap Exposure */}
                <div>
                  <h3 className="text-base font-bold mb-3">
                    Market Cap Exposure
                  </h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.market_cap_exposure}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" tick={{ fontSize: 9 }} angle={-15} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="weight" fill="#A8A67A" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-8 left-12 text-sm text-slate-500">1</div>
          </div>

          {/* Page 2 */}
          <div className="p-12 border-t-2" style={{ minHeight: '297mm', position: 'relative' }}>
            {/* Fund Commentary */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 uppercase">Fund Commentary</h2>
              <p className="text-sm leading-relaxed text-justify">
                {data.commentary?.fund_commentary}
              </p>
            </div>

            {/* Key Contributor */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 uppercase">Key Contributor</h2>
              <p className="text-sm leading-relaxed text-justify">
                <span className="font-bold">{data.commentary?.key_contributor?.company}</span>{' '}
                {data.commentary?.key_contributor?.text}
              </p>
            </div>

            {/* Key Detractor */}
            {data.commentary?.key_detractor && (
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4 uppercase">Key Detractor</h2>
                <p className="text-sm leading-relaxed text-justify">
                  <span className="font-bold">{data.commentary?.key_detractor?.company}</span>{' '}
                  {data.commentary?.key_detractor?.text}
                </p>
              </div>
            )}

            {/* Outlook */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 uppercase">Outlook</h2>
              <p className="text-sm leading-relaxed text-justify">
                {data.commentary?.outlook}
              </p>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-8 left-12 text-sm text-slate-500">2</div>
          </div>

          {/* Page 3 */}
          <div className="p-12 border-t-2" style={{ minHeight: '297mm', position: 'relative' }}>
            <div className="grid grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-lg font-bold mb-4">Contact Us</h3>
                <div className="text-sm space-y-2">
                  <p className="font-bold">Sydney</p>
                  <p>Level 11, 179 Elizabeth Street,</p>
                  <p>Sydney, NSW 2000</p>
                  <p>+612 9021 7701</p>
                  <p>info@ellerstoncapital.com</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Find out more:</h3>
                <p className="text-sm leading-relaxed">
                  Should investors have any questions or queries regarding the Fund, please contact our
                  Investor Relations team on 02 9021 7701 or info@ellerstoncapital.com or visit us at
                  ellerstoncapital.com.
                </p>
                <p className="text-sm leading-relaxed mt-4">
                  All holding enquiries should be directed to our registry, Automic Group on 1300 101 595
                  or ellerstonfunds@automicgroup.com.au.
                </p>
              </div>
            </div>

            <div className="text-xs leading-relaxed text-slate-600">
              <p className="mb-2">
                This report has been prepared by Ellerston Capital Limited ABN 34 110 397 674 AFSL 283 000
                (Manager), as the responsible entity of the Ellerston Australian Emerging Leaders Fund Class A
                Units ARSN 647 979 333 without taking account the objectives, financial situation or needs of
                individuals. Before making an investment decision about the Fund, persons should read the Fund's
                Product Disclosure Statement and Target Market Determination (TMD) which can be obtained from the
                Manager's website www.ellerstoncapital.com or by contacting info@ellerstoncapital.com and
                obtaining advice from an appropriate financial adviser.
              </p>
              <p>
                This material has been prepared based on information believed to be accurate at the time of
                publication. Assumptions and estimates may have been made which may prove not to be accurate.
                Ellerston Capital undertakes no responsibility to correct any such inaccuracy. Past performance is
                not a reliable indicator of future performance.
              </p>
            </div>

            {/* Footer */}
            <div className="absolute bottom-8 left-12 flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_8cf0233c-34c4-4c9e-9fe0-7f998765066d/artifacts/key4qenq_Logo.png"
                alt="Ellerston Capital"
                className="h-8"
              />
              <span className="text-sm text-slate-500">3</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;